﻿//Current version 3.2
import { getRealm, getAccessToken, refreshToken, doLogout, handleError, defaultRealm } from '../plugins/authentication'
import { scopeUrl } from './assetClassScope'

class _PrivateFetchClass {
  constructor() {
    let globalErrorCallback = (error) => {
      return { propagate: true, error }
    }

    this.makeRequest = (url, method, data, formDataMgmt = false, binary = false, keep403 = false) => {
      return fetchWrapper(url, method, data, formDataMgmt, binary).catch((error) =>
        handleRequestError(error, url, method, data, formDataMgmt, false, binary, keep403)
      )
    }

    /**
     * A 401 can mean two different things: an expired token, or a valid token that is not
     * authorized for that resource. We tell them apart by the token value after the renewal — if
     * it changed the token had expired and the request must be replayed, if it stayed the same
     * that 401 was about authorization and logging out would be wrong. Only a failed renewal
     * ends the session.
     */
    const handleRequestError = (error, url, method, data, formDataMgmt, isRetry, binary = false, keep403 = false) => {
      // There is no response at all on a network (or CORS) error: without this guard the catch
      // itself would throw, leaving the page hanging with no message.
      const status = error && error.response ? error.response.status : null

      if (status === 401) {
        if (isRetry) {
          // The token had just been renewed and is rejected anyway: this is not an expiry.
          return handleError('403')
        }
        return refreshToken().then(
          (tokenChanged) => {
            if (!tokenChanged) {
              return handleError('403')
            }
            return fetchWrapper(url, method, data, formDataMgmt, binary).catch((retryError) =>
              handleRequestError(retryError, url, method, data, formDataMgmt, true, binary, keep403)
            )
          },
          () => doLogout()
        )
      }

      // keep403: the caller tells the user about a refusal itself (see post), instead of the error page.
      if (status === 403 && !keep403) {
        return handleError('403')
      }

      let ret = globalErrorCallback(error)
      if (ret && ret.propagate && ret.error !== undefined) {
        throw ret.error
      } else {
        throw error
      }
    }

    const fetchWrapper = (url, method, data, formDataMgmt = false, binary = false) => {
      // Scope the (relative) API path to the selected asset class before the basePath is prepended.
      // No-op for cross endpoints and when no asset class is selected; idempotent on retries.
      url = scopeUrl(url)
      const realm = getRealm()
      const basePath = realm === defaultRealm ? process.env.VUE_APP_API_ENDPOINT : window.location.origin

      if (url.search(/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&\/=]*)/) === -1) {
        url = basePath + url
      }

      const accessToken = getAccessToken()
      const params = {
        method,
        headers: {
          'X-Tenant': realm,
          'Referrer-Policy': 'strict-origin-when-cross-origin',
          Authorization: accessToken ? 'Bearer ' + accessToken : undefined,
        },
        // credentials: 'include',
      }

      if (method === 'GET') {
        url = new URL(url)
        // add data to query string
        if (data) {
          if (!(data instanceof Array)) {
            //Flattering needed
            data = flatData(data)
          }
          url.search = new URLSearchParams(data)
        }
      } else {
        if (!formDataMgmt) {
          params.body = JSON.stringify(data)
          params.headers['Content-Type'] = 'application/json'
        } else {
          params.body = data
        }
      }

      if (binary) {
        // The file is served as is (not as JSON): hand the blob back with the name the server chose.
        return fetch(url, params)
          .then(checkResponseStatus)
          .then((response) =>
            response.blob().then((blob) => ({
              blob,
              filename: filenameFromContentDisposition(response.headers.get('Content-Disposition')),
            }))
          )
      }

      return fetch(url, params)
        .then(checkResponseStatus)
        .then((response) => response.text())
        .then((dat) => (dat ? JSON.parse(dat) : {}))
    }
  }
}

function filenameFromContentDisposition(header) {
  if (!header) {
    return null
  }
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8) {
    return decodeURIComponent(utf8[1])
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain ? plain[1] : null
}

/** GET of a file served as is (not JSON). Resolves { blob, filename }. */
export function getFile(url, data) {
  return _PrivateFetch.makeRequest(url, 'GET', data, false, true)
}

export function get(url, data) {
  return _PrivateFetch.makeRequest(url, 'GET', data)
}

/**
 * `options.keep403`: a 403 is thrown to the caller, who tells the user, instead of redirecting to the error
 * page. Off by default: for most requests a 403 does mean a page the user may not see.
 */
export function post(url, data, options = {}) {
  return _PrivateFetch.makeRequest(url, 'POST', data, false, false, options.keep403 === true)
}

export function postFormData(url, data, useGlobalErrorCallback = true) {
  return _PrivateFetch.makeRequest(url, 'POST', data, useGlobalErrorCallback, false, true)
}

export function fetchCustomMethod(url, method, data) {
  return _PrivateFetch.makeRequest(url, method, data)
}

const _PrivateFetch = new _PrivateFetchClass()

function flatData(data) {
  // flat data --> avoid bad object by array conversion
  const retVal = []
  Object.keys(data).forEach((key) => {
    const val = data[key]
    if (val instanceof Array) {
      val.forEach((innerValue) => retVal.push([key, innerValue]))
    } else {
      retVal.push([key, val])
    }
  })
  return retVal
}

function checkResponseStatus(response) {
  if (response.ok) {
    return response
  } else {
    const error = new Error(response.statusText)
    error.response = response
    throw error
  }
}
